import React from 'react';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function AvenorDocs() {
  const glassShellClass =
    'rounded-2xl border border-white/50 bg-white/55 shadow-[0_20px_70px_rgba(59,130,246,0.18)] backdrop-blur-xl';
  const glassSectionClass = 'border-b border-white/45 bg-white/45 backdrop-blur-xl';
  const glassCardClass =
    'rounded-xl border border-white/50 bg-white/70 p-4 shadow-[0_10px_35px_rgba(76,29,149,0.14)] backdrop-blur';

  const topButtons = [
    {
      name: 'Buy',
      description: 'Open credit packages and top up your sales credits before running paid AI actions.',
      when: 'Use this first if your credit balance is low or zero.',
    },
    {
      name: 'SMTP',
      description: 'Configure sender host, port, username, password, sender email, and secure mode.',
      when: 'Use before sending any initial outreach emails.',
    },
    {
      name: 'Settings',
      description: 'Update company profile data used in emails and update your account password.',
      when: 'Use when branding or user credentials need refreshing.',
    },
    {
      name: 'Docs',
      description: 'Open this tutorial page for process help and button explanations.',
      when: 'Use when onboarding new users or validating workflow steps.',
    },
  ];

  const smartActions = [
    {
      name: 'Enrich',
      description: 'Runs AI enrichment on leads and consumes credits based on enrichment volume.',
      result: 'Improves lead quality with richer context for outreach and reporting.',
    },
    {
      name: 'Filter',
      description: 'Shows only leads marked as filtered/enriched so you can focus on qualified records.',
      result: 'Reduces noise and speeds up targeting.',
    },
    {
      name: 'Companies House Hit',
      description: 'Shows leads that matched Companies House data.',
      result: 'Prioritizes records with stronger verification confidence.',
    },
    {
      name: 'All Locations',
      description: 'Limits visible leads to a selected location in the active result set.',
      result: 'Lets teams run geo-based outreach batches.',
    },
    {
      name: 'Clear Location',
      description: 'Removes location filtering and returns to the full active set.',
      result: 'Resets your view before the next pass.',
    },
  ];

  const quickStart = [
    'Check credits in the header area. If low, use Buy first.',
    'Open SMTP and run a connection check so sending works before drafting outreach.',
    'Create leads using New Lead Pull (free) or AI pulls if you need broader discovery.',
    'Apply Filter and Companies House Hit to narrow to high-confidence prospects.',
    'Use Enrich on the best candidates before generating scripts or sending email.',
    'Move contacted opportunities forward with Continue to open the Avenor pipeline.',
  ];

  const dailyWorkflow = [
    {
      title: '1) Prepare your account',
      detail:
        'Confirm SMTP, company profile, and credit balance. This prevents failed sends and interrupted workflows later in the session.',
    },
    {
      title: '2) Build the lead set',
      detail:
        'Use manual location + sector for precise pulls, or AI Town/Sector pull when you want broader market discovery.',
    },
    {
      title: '3) Validate quality fast',
      detail:
        'Use Filter and Companies House Hit toggles to isolate higher-signal leads before spending credits.',
    },
    {
      title: '4) Enrich intentionally',
      detail:
        'Run enrichment on shortlisted records, not the entire table, to conserve credits and increase outreach relevance.',
    },
    {
      title: '5) Personalise outreach',
      detail:
        'Use Edit for the initial email draft and Generate Call Script for call preparation on priority leads.',
    },
    {
      title: '6) Execute in batches',
      detail:
        'Use Send by Sector/Location for grouped sending. Start with small test batches, review replies, then scale.',
    },
    {
      title: '7) Track and move forward',
      detail:
        'Update progress statuses consistently and use Continue on contacted leads to open the pipeline and continue deal management.',
    },
  ];

  const troubleshooting = [
    {
      issue: 'Emails are not sending',
      fix: 'Re-open SMTP, verify host/port/secure mode, confirm credentials, and run connection check again.',
    },
    {
      issue: 'AI actions are unavailable',
      fix: 'Check account permission level and credit balance. Read Only users cannot run AI or enrichment actions.',
    },
    {
      issue: 'No leads appear after filtering',
      fix: 'Clear location/sector/date filters one by one and disable Filter/Companies House Hit toggles to widen results.',
    },
    {
      issue: 'Cannot proceed to pipeline',
      fix: 'Continue appears for contacted leads with valid Avenor access. Mark progress/contact first, then continue.',
    },
  ];

  return (
    <div className="w-full overflow-hidden">
      <div className={classNames('w-full overflow-hidden', glassShellClass)}>
        <div className={classNames(glassSectionClass, 'px-4 py-5 sm:px-6')}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-bold text-slate-900 sm:text-xl">Avenor Leads — Full Tutorial</h2>
              <p className="mt-1 text-sm text-slate-700">
                End-to-end guide covering setup, lead generation, qualification, outreach, reporting, and pipeline handoff.
              </p>
            </div>
            <a
              href="/avenor"
              className="rounded-lg bg-indigo-700 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-600"
            >
              Open Avenor
            </a>
          </div>
        </div>

        <div className="space-y-4 px-4 py-5 sm:px-6">
          <section className={glassCardClass}>
            <h3 className="text-base font-semibold text-slate-900">Quick Start (5 Minutes)</h3>
            <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm text-slate-700">
              {quickStart.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ol>
          </section>

          <section className={glassCardClass}>
            <h3 className="text-base font-semibold text-slate-900">Daily Workflow</h3>
            <div className="mt-3 space-y-3">
              {dailyWorkflow.map((step) => (
                <div key={step.title} className="rounded-lg border border-white/60 bg-white/80 p-3">
                  <p className="text-sm font-semibold text-slate-900">{step.title}</p>
                  <p className="mt-1 text-xs text-slate-700 sm:text-sm">{step.detail}</p>
                </div>
              ))}
            </div>
          </section>

          <section className={glassCardClass}>
            <h3 className="text-base font-semibold text-slate-900">Top Row Buttons</h3>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {topButtons.map((item) => (
                <div key={item.name} className="rounded-lg border border-white/60 bg-white/80 p-3">
                  <p className="text-sm font-semibold text-slate-900">{item.name}</p>
                  <p className="mt-1 text-xs text-slate-700 sm:text-sm">{item.description}</p>
                  <p className="mt-1 text-xs text-slate-600 sm:text-sm">
                    <span className="font-semibold text-slate-800">When to use:</span> {item.when}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className={glassCardClass}>
            <h3 className="text-base font-semibold text-slate-900">Lead Actions Area</h3>
            <div className="mt-3 space-y-2 text-sm text-slate-700">
              <p>
                <span className="font-semibold text-slate-900">New Lead Pull (Free):</span> Pull leads using manual
                Location and Sector. Best for focused, repeatable targeting with no credit spend.
              </p>
              <p>
                <span className="font-semibold text-slate-900">AI Town Pull / AI Sector Pull:</span> Generate AI
                suggestions when you need discovery outside your usual target lists.
              </p>
              <p>
                <span className="font-semibold text-slate-900">AI Pull Leads:</span> Pull across selected AI
                location/sector combinations. Use carefully — wide combinations can consume credits quickly.
              </p>
              <p>
                <span className="font-semibold text-slate-900">Recommended pattern:</span> Start narrow, validate
                quality, then expand by adding one new town or sector batch at a time.
              </p>
            </div>
          </section>

          <section className={glassCardClass}>
            <h3 className="text-base font-semibold text-slate-900">Smart Actions (Filters)</h3>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {smartActions.map((item) => (
                <div key={item.name} className="rounded-lg border border-white/60 bg-white/80 p-3">
                  <p className="text-sm font-semibold text-slate-900">{item.name}</p>
                  <p className="mt-1 text-xs text-slate-700 sm:text-sm">{item.description}</p>
                  <p className="mt-1 text-xs text-slate-600 sm:text-sm">
                    <span className="font-semibold text-slate-800">Outcome:</span> {item.result}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className={glassCardClass}>
            <h3 className="text-base font-semibold text-slate-900">Reports &amp; Bulk Operations</h3>
            <div className="mt-3 space-y-2 text-sm text-slate-700">
              <p>
                <span className="font-semibold text-slate-900">AI Reports:</span> Review historical analysis or
                generate a fresh report after major lead or enrichment updates.
              </p>
              <p>
                <span className="font-semibold text-slate-900">Send by Sector/Location:</span> Send in controlled
                grouped batches; monitor replies before increasing volume.
              </p>
              <p>
                <span className="font-semibold text-slate-900">Delete selected leads:</span> Clean bad data carefully.
                Always validate filters first to avoid removing unintended records.
              </p>
            </div>
          </section>

          <section className={glassCardClass}>
            <h3 className="text-base font-semibold text-slate-900">Lead Row Buttons</h3>
            <div className="mt-3 space-y-2 text-sm text-slate-700">
              <p>
                <span className="font-semibold text-slate-900">Enrich Lead (3 Credits):</span> Best on shortlisted
                leads where extra context can increase conversion likelihood.
              </p>
              <p>
                <span className="font-semibold text-slate-900">Generate Call Script — 1 Credit:</span> Create a call
                talk-track before live outreach so reps stay consistent.
              </p>
              <p>
                <span className="font-semibold text-slate-900">Edit:</span> Open and adjust the initial email draft for
                personalisation before send.
              </p>
              <p>
                <span className="font-semibold text-slate-900">Continue:</span> For eligible contacted leads, opens the
                Avenor pipeline to continue deal workflow.
              </p>
              <p>
                <span className="font-semibold text-slate-900">Delete:</span> Remove low-quality or duplicate rows one
                by one.
              </p>
            </div>
          </section>

          <section className={glassCardClass}>
            <h3 className="text-base font-semibold text-slate-900">Credit Strategy</h3>
            <div className="mt-3 space-y-2 text-sm text-slate-700">
              <p>
                <span className="font-semibold text-slate-900">Protect credits:</span> Filter and qualify first, then
                enrich/script on high-priority leads only.
              </p>
              <p>
                <span className="font-semibold text-slate-900">Batch intelligently:</span> Run small AI pulls, check
                quality, then scale. Avoid very broad town × sector combinations upfront.
              </p>
              <p>
                <span className="font-semibold text-slate-900">Measure outcomes:</span> Compare reply/meeting rates
                from enriched vs non-enriched cohorts to guide spend.
              </p>
            </div>
          </section>

          <section className={glassCardClass}>
            <h3 className="text-base font-semibold text-slate-900">Troubleshooting</h3>
            <div className="mt-3 space-y-3">
              {troubleshooting.map((item) => (
                <div key={item.issue} className="rounded-lg border border-white/60 bg-white/80 p-3">
                  <p className="text-sm font-semibold text-slate-900">{item.issue}</p>
                  <p className="mt-1 text-xs text-slate-700 sm:text-sm">{item.fix}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
